import Layout from '@comp/layout'
import SolutionMeta from '@comp/solution/solution-meta'
import SolutionHero from '@comp/solution/solution-hero'
import HowToSteps, { HowToStep } from '@comp/learn/how-to-steps'
import SolutionRelated, { RelatedItem } from '@comp/solution/solution-related'
import FinalCta from '@comp/home/final-cta'

interface PageContent {
  metaTitle: string
  metaDescription: string
  heroEyebrow: string
  heroTitle: [string, string]
  heroBody: string
  badges: string[]
  primaryCta: string
  secondaryCta: string
  stepsEyebrow: string
  stepsTitle: string
  stepsBody: string
  docLabel: string
  steps: HowToStep[]
  relatedEyebrow: string
  relatedTitle: string
  related: RelatedItem[]
}

const content: Record<'en' | 'tr', PageContent> = {
  en: {
    metaTitle: 'How to migrate from ADFS',
    metaDescription:
      'Retire your ADFS farm without breaking a single app: inventory relying-party trusts, stand up Monosign next to AD, re-point applications one by one, move MFA policies, and decommission ADFS last.',
    heroEyebrow: 'How-to · Migrate from ADFS',
    heroTitle: ['Leave ADFS behind.', 'Keep AD. Keep every app.'],
    heroBody:
      'ADFS gave you federation; it also gave you a server farm to patch, certificates to rotate, and claim rules nobody wants to touch. The migration is less dramatic than it sounds: AD stays the source of truth, apps move one at a time, and the farm is the last thing you turn off.',
    badges: ['AD stays source of truth', 'Per-app cutover', '~7 minutes to read'],
    primaryCta: 'Start free trial →',
    secondaryCta: 'Open the full docs',
    stepsEyebrow: 'Walkthrough',
    stepsTitle: 'From ADFS farm to a decommission ticket.',
    stepsBody:
      'Six steps, in the order that keeps every relying party working while you move.',
    docLabel: 'Continue in the full docs',
    steps: [
      {
        title: 'Inventory your relying-party trusts',
        body: 'Export the full list of relying-party trusts from ADFS (Get-AdfsRelyingPartyTrust in PowerShell gives you everything, including the claim rules). This inventory is the migration plan: every trust is an app that needs a new home, and the sign-in logs tell you which ones are actually still used.',
        hint: 'Trusts with no sign-ins in the last 90 days are candidates for retirement, not migration. Shrink the list before you move it.',
      },
      {
        title: 'Map each app: SAML, WS-Fed, and claims',
        body: 'For every live trust, record the protocol (SAML 2.0 or WS-Federation), the endpoints and identifiers, and which claims the app actually consumes. Most claim-rule complexity turns out to be attribute mapping (name, UPN, groups) that any modern IdP expresses in a few lines.',
      },
      {
        title: 'Stand up Monosign next to AD',
        body: 'Deploy Monosign (self-hosted, in minutes) and connect Active Directory as its identity source. AD stays the source of truth for users, passwords, and groups; Monosign reads from it and becomes the new federation front door. Nothing about your directory changes.',
        hint: 'Because AD remains authoritative, this step is zero-risk to production: ADFS and Monosign serve side by side against the same directory.',
      },
      {
        title: 'Re-point apps one by one: pilot first',
        body: 'Pick one low-stakes internal app as the pilot. Register it in Monosign with the SAML or WS-Fed settings you mapped, update the app to trust Monosign, and test the round-trip with real users. Then work through the inventory app by app; each cutover is independent and reversible.',
      },
      {
        title: 'Move MFA and conditional access policies',
        body: 'Recreate your ADFS access control policies and MFA requirements in Monosign: per-app MFA, network conditions, and group scoping. Do this once the apps riding on those policies have moved, so a policy never lives in two engines at the same time.',
      },
      {
        title: 'Decommission the ADFS farm last',
        body: 'When the sign-in log on ADFS goes quiet, you are done: remove the relying-party trusts, retire the WAP proxies and the farm, and reclaim the certificates and servers. ADFS is the last thing you switch off, not the first.',
      },
    ],
    relatedEyebrow: 'Keep exploring',
    relatedTitle: 'Related Monofor capabilities.',
    related: [
      { label: 'Active Directory sync', description: 'Connect AD as a Monosign identity source.', href: '/learn/active-directory-sync' },
      { label: 'Single Sign-On', description: 'SAML, OIDC, and WS-Fed federation in Monosign.', href: '/sign/sso' },
      { label: 'Multi-Factor Authentication', description: 'The MFA policies your apps move onto.', href: '/sign/mfa' },
    ],
  },
  tr: {
    metaTitle: 'ADFS’ten nasıl geçilir',
    metaDescription:
      'ADFS çiftliğinizi tek bir uygulamayı bozmadan emekliye ayırın: relying-party trust envanteri çıkarın, Monosign’ı AD’nin yanına kurun, uygulamaları tek tek taşıyın, MFA politikalarını aktarın ve ADFS’i en son kapatın.',
    heroEyebrow: 'Rehber · ADFS’ten geçiş',
    heroTitle: ['ADFS’i geride bırakın.', 'AD kalsın. Her uygulama kalsın.'],
    heroBody:
      'ADFS size federasyon verdi; yanında yamalanacak bir sunucu çiftliği, döndürülecek sertifikalar ve kimsenin dokunmak istemediği claim kuralları da verdi. Geçiş göründüğünden daha az sancılı: AD doğruluk kaynağı olarak kalır, uygulamalar tek tek taşınır ve çiftlik en son kapatılır.',
    badges: ['AD doğruluk kaynağı kalır', 'Uygulama bazlı geçiş', '~7 dakikalık okuma'],
    primaryCta: 'Ücretsiz deneme başlat →',
    secondaryCta: 'Dokümantasyonu açın',
    stepsEyebrow: 'Adım adım',
    stepsTitle: 'ADFS çiftliğinden kapatma talebine.',
    stepsBody:
      'Altı adım: geçiş boyunca her relying party’yi çalışır tutan sırayla.',
    docLabel: 'Rehberin tamamına gidin',
    steps: [
      {
        title: 'Relying-party trust envanterini çıkarın',
        body: 'ADFS’teki tüm relying-party trust listesini dışa aktarın (PowerShell’de Get-AdfsRelyingPartyTrust, claim kuralları dahil her şeyi verir). Bu envanter geçiş planınızdır: her trust, yeni bir eve taşınacak bir uygulamadır ve oturum açma logları hangilerinin gerçekten kullanıldığını söyler.',
        hint: 'Son 90 günde hiç oturum açılmamış trust’lar taşınmaya değil, emekliye ayrılmaya adaydır. Listeyi taşımadan önce küçültün.',
      },
      {
        title: 'Her uygulamayı eşleyin: SAML, WS-Fed ve claim’ler',
        body: 'Aktif her trust için protokolü (SAML 2.0 veya WS-Federation), endpoint ve tanımlayıcıları, uygulamanın gerçekten tükettiği claim’leri kaydedin. Claim kuralı karmaşıklığının çoğu, modern bir IdP’nin birkaç satırla ifade ettiği öznitelik eşlemesinden (ad, UPN, gruplar) ibaret çıkar.',
      },
      {
        title: 'Monosign’ı AD’nin yanına kurun',
        body: 'Monosign’ı kurun (self-hosted, dakikalar içinde) ve Active Directory’yi kimlik kaynağı olarak bağlayın. Kullanıcılar, parolalar ve gruplar için doğruluk kaynağı AD olarak kalır; Monosign ondan okur ve yeni federasyon kapısı olur. Dizininizde hiçbir şey değişmez.',
        hint: 'AD yetkili kaynak olarak kaldığı için bu adım üretim açısından risksizdir: ADFS ve Monosign aynı dizine karşı yan yana hizmet verir.',
      },
      {
        title: 'Uygulamaları tek tek taşıyın: önce pilot',
        body: 'Pilot olarak düşük riskli bir iç uygulama seçin. Eşlediğiniz SAML veya WS-Fed ayarlarıyla uygulamayı Monosign’a kaydedin, uygulamayı Monosign’a güvenecek şekilde güncelleyin ve gerçek kullanıcılarla uçtan uca test edin. Sonra envanteri uygulama uygulama ilerleyin; her geçiş bağımsız ve geri alınabilirdir.',
      },
      {
        title: 'MFA ve koşullu erişim politikalarını taşıyın',
        body: 'ADFS erişim kontrol politikalarınızı ve MFA gereksinimlerinizi Monosign’da yeniden oluşturun: uygulama başına MFA, ağ koşulları ve grup kapsamı. Bunu, o politikalara bağlı uygulamalar taşındıktan sonra yapın; bir politika aynı anda iki motorda yaşamasın.',
      },
      {
        title: 'ADFS çiftliğini en son kapatın',
        body: 'ADFS’in oturum açma logu sessizleştiğinde iş bitmiştir: relying-party trust’ları kaldırın, WAP proxy’leri ve çiftliği emekliye ayırın, sertifikaları ve sunucuları geri kazanın. ADFS ilk değil, en son kapattığınız şeydir.',
      },
    ],
    relatedEyebrow: 'Keşfetmeye devam edin',
    relatedTitle: 'İlgili Monofor yetenekleri.',
    related: [
      { label: 'Active Directory senkronizasyonu', description: "AD'yi Monosign kimlik kaynağı olarak bağlayın.", href: '/learn/active-directory-sync' },
      { label: 'Tek Oturum Açma (SSO)', description: "Monosign'da SAML, OIDC ve WS-Fed federasyonu.", href: '/sign/sso' },
      { label: 'Çok Faktörlü Kimlik Doğrulama', description: 'Uygulamalarınızın taşınacağı MFA politikaları.', href: '/sign/mfa' },
    ],
  },
}

type Props = { locale: string }

export default function MigrateFromAdfsPage({ locale }: Props) {
  const c = locale === 'tr' ? content.tr : content.en
  return (
    <Layout>
      <SolutionMeta
        title={c.metaTitle}
        description={c.metaDescription}
        path="/learn/migrate-from-adfs"
        locale={locale}
        bilingual
      />
      <main className="bg-surface text-ink-primary">
        <SolutionHero
          eyebrow={c.heroEyebrow}
          title={
            <>
              {c.heroTitle[0]}
              <br />
              {c.heroTitle[1]}
            </>
          }
          body={c.heroBody}
          badges={c.badges}
          primaryCta={{ label: c.primaryCta, href: '/try/now' }}
          secondaryCta={{ label: c.secondaryCta, href: 'https://help.monofor.com/monosign/configuration-directory-sources-ad' }}
        />
        <HowToSteps
          eyebrow={c.stepsEyebrow}
          title={<>{c.stepsTitle}</>}
          body={c.stepsBody}
          steps={c.steps}
          schemaName={c.metaTitle}
          docHref="https://help.monofor.com/monosign/configuration-directory-sources-ad"
          docLabel={c.docLabel}
        />
        <SolutionRelated
          eyebrow={c.relatedEyebrow}
          title={c.relatedTitle}
          items={c.related}
        />
        <FinalCta />
      </main>
    </Layout>
  )
}

export async function getStaticProps({ locale }: { locale: string }) {
  return { props: { locale: locale ?? 'en' } }
}
